import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GrpcFactoryService } from '../../sharedServices/grpc-factory.service';
import { UserCompanyControllerClient } from '../../api/ph-web/user/user.apiServiceClientPb';
import {
  GetIdentificationTypeRequest,
  GetIdentificationTypeResponse,
  GetBillingDataRequest,
  GetBillingDataResponse,
  UpdateBillingDataRequest,
  UpdateBillingDataResponse,
  GetBalanceRequest,
  GetBalanceResponse,
  StatusInfo,
  GetUserTermsConditionsRequest,
  GetUserTermsConditionsResponse,
  GetUserTermsConditionsCheckRequest,
  AcceptUserTermsConditionsRequest,
  GetUserTermsConditionsCheckResponse,
  TermsType,
  ValidateEmailUserRequest,
  GetProfileDataRequest,
  GetProfileDataResponse,
  ProfileUserData,
  ProfileCompanyData,
  UpdateUserProfileDataRequest,
  UpdateProfileDataResponse,
  UpdateCompanyProfileDataRequest,
} from '../../api/ph-web/user/user.api_pb';
import { ItemNumberToFilterShared } from '../../models/interface/itemNumberToFilter.interface';
import { UserType as UserType } from '../../models/interface/userType.enum';
import { BillingDataShared } from '../../models/interface/billingData.interface';
import { sharedUsersConstants } from '../../sharedConstants/users.constants';
import { UserDataShared } from '../../models/interface/userData.interface';
import { MuException } from '../../types/exception.type';

@Injectable({
  providedIn: 'root',
})
export class MuUserService {
  private userCompanyClient = this.grpcFactoryService.create(
    UserCompanyControllerClient,
    `${environment.hostname}${environment.hostPort}`,
    'UserCompanyControllerClient'
  );
  private getDocumentTypeListCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.getIdentificationType, {
    callName: 'getIdentificationType',
  });
  private getBillingDataCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.getBillingData, {
    callName: 'getBillingData',
  });
  private updateBillingDataCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.updateBillingData, {
    callName: 'updateBillingData',
  });
  private getBalanceCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.getBalance, {
    callName: 'getBalance',
  });
  private getTermsCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.getUserTermsConditions, {
    callName: 'getUserTermsConditions',
  });
  private checkTermsCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.getUserTermsConditionsCheck, {
    callName: 'getUserTermsConditionsCheck',
  });
  private acceptTermsCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.acceptUserTermsConditions, {
    callName: 'acceptUserTermsConditions',
  });
  private updateTermsCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.updateUserTermsConditions, {
    callName: 'updateUserTermsConditions',
  });
  private updateUserDataCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.updateBillingData, {
    callName: 'updateBillingData',
  });
  private validateEmailCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.validateEmailUser, {
    callName: 'validateEmailUser',
  });
  private getProfileDataCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.getProfileData, {
    callName: 'getProfileData',
  });
  private updateUserProfileCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.updateUserProfileData, {
    callName: 'updateUserProfileData',
  });
  private updateCompanyProfileCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.updateCompanyProfileData, {
    callName: 'updateCompanyProfileData',
  });

  constructor(private grpcFactoryService: GrpcFactoryService) {
    /** */
  }

  /**
   * get document list
   * @param {number} countryId
   * @param {number} accountType
   * @returns {ItemNumberToFilterShared[]}
   */
  async getDocumentTypeList(countryId: number, accountType: number): Promise<ItemNumberToFilterShared[]> {
    const request: GetIdentificationTypeRequest = new GetIdentificationTypeRequest();
    request.setCountryid(countryId);
    request.setUsertype(accountType as unknown as UserType);
    const response: GetIdentificationTypeResponse = await this.getDocumentTypeListCall(request);
    return response?.getIdentificationstypeList()?.map(type => {
      return {
        value: type.getId(),
        label: `${type.getAbbreviation()} - ${type.getName()}`,
        extra: type.getAbbreviation(),
      };
    });
  }

  /**
   * get billing data user
   *
   * @param {number} userId
   * @return {*}  {Promise<BillingDataShared>}
   * @memberof MuUserService.getBillingData
   */
  async getBillingData(userId: number): Promise<BillingDataShared> {
    const request: GetBillingDataRequest = new GetBillingDataRequest();
    request.setUserid(userId);

    const response: GetBillingDataResponse = await this.getBillingDataCall(request);

    if (!response || response?.getStatusinfo()?.getErrorcode() !== StatusInfo.ErrorCode.OK) {
      throw new Error('');
    }

    return {
      documentType: response.getDocumenttype(),
      documentNumber: response.getDocumentnumber(),
      email: response.getEmail(),
      isuserupdated: response.getIsuserupdated(),
    };
  }

  /**
   * update data billing user
   *
   * @param {BillingDataShared} userData
   * @return {*}  {Promise<boolean>}
   * @memberof MuUserService.updateBillingData
   */
  async updateBillingData(userData: BillingDataShared): Promise<boolean> {
    const request: UpdateBillingDataRequest = new UpdateBillingDataRequest();
    request.setUserid(userData.userId);
    request.setDocumenttype(userData.documentType);
    request.setDocumentnumber(userData.documentNumber);
    request.setEmail(userData.email);

    const response: UpdateBillingDataResponse = await this.updateBillingDataCall(request);

    if (!response || typeof response?.getStatus !== 'function') {
      throw new Error('');
    }

    return response.getStatus().getErrorcode() === 1;
  }

  /**
   * get balance
   * @param {number} userId
   * @returns {number} response
   */
  async getBalance(userId: number): Promise<number> {
    const request: GetBalanceRequest = new GetBalanceRequest();
    request.setUserid(userId);
    const response: GetBalanceResponse = await this.getBalanceCall(request);

    return response.getBalance();
  }

  /**
   * get terms
   * @param {number} countryId
   * @param {number} accountType
   * @param {TermsType} TermsType
   * @returns {string} response
   */
  async getTermsOrPolitics(countryId: number, accountType: UserType, termsType: TermsType): Promise<string> {
    const request: GetUserTermsConditionsRequest = new GetUserTermsConditionsRequest();
    request.setCountryid(countryId);
    request.setDocumenttype(termsType);
    request.setUsertype(accountType);
    request.setFilter(sharedUsersConstants.termsFilter);

    const response: GetUserTermsConditionsResponse = await this.getTermsCall(request);

    return response.getTermsconditionsList()[0].getUrl();
  }

  /**
   * check terms
   * @param {number} userId
   * @param {TermsType[]} termsTypeList
   * @returns {boolean} response
   */
  async checkTermsAndPolitics(userId: number, termsTypeList: TermsType[]): Promise<boolean> {
    const request: GetUserTermsConditionsCheckRequest = new GetUserTermsConditionsCheckRequest();
    request.setUserid(userId);
    request.setDocumenttypeList(termsTypeList);

    const response: GetUserTermsConditionsCheckResponse = await this.checkTermsCall(request);

    return response.getTermsconditionscheck().getTermsandconditions() && response.getTermsconditionscheck().getDataprivacypolicy();
  }

  /**
   * accept or update terms
   * @param {number} userId
   */
  async acceptOrUpdateTerms(userId: number, update?: boolean): Promise<void> {
    const request: AcceptUserTermsConditionsRequest = new AcceptUserTermsConditionsRequest();
    request.setDocumenttypeList(
      !update ? [TermsType.TERMS_AND_CONDITIONS] : [TermsType.TERMS_AND_CONDITIONS, TermsType.DATA_PRIVACY_POLICY]
    );
    request.setUserid(userId);

    /*eslint-disable-next-line*/
    update ? await this.updateTermsCall(request) : await this.acceptTermsCall(request);
  }

  /**
   * update user data
   *
   * @param {UserData} userData
   */
  async updateUserData(userId: number, userData: UserDataShared): Promise<void> {
    const request: UpdateBillingDataRequest = new UpdateBillingDataRequest();
    request.setUserid(userId);
    request.setCountryid(userData.countryId);
    await this.updateUserDataCall(request);
  }

  /**
   * method to validate if email exist
   *
   * @param {string} email
   * @return {boolean} response
   */
  async validateIfEmailExist(email: string): Promise<boolean> {
    const validateEmailRequest = new ValidateEmailUserRequest();
    validateEmailRequest.setEmail(email);
    const response = await this.validateEmailCall(validateEmailRequest);
    return response?.getStatusinfo().getStatus() !== StatusInfo.StatusType.SUCCESS;
  }

  /**
   * get user profile data
   * @param {number} userId
   * @returns {UserDataShared} response
   */
  async getProfileData(userId: number): Promise<UserDataShared> {
    const request: GetProfileDataRequest = new GetProfileDataRequest();
    request.setUserid(userId);
    const response: GetProfileDataResponse = await this.getProfileDataCall(request);
    const user: ProfileUserData = response.getUser();
    const company: ProfileCompanyData = response.getCompany();
    return {
      name: user.getName(),
      email: user.getEmail(),
      userPhone: user.getPhone(),
      countryId: user.getCountryid(),
      cityId: user.getCityid(),
      documentType: user.getDocumenttype(),
      documentNumber: user.getDocumentnumber(),
      postalcode: user.getPostalcode(),
      companyName: company ? company.getName() : '',
      companyPhone: company ? company.getPhone() : '',
      companyDocumentType: company ? company.getDocumenttype() : 0,
      companyDocumentNumber: company ? company.getDocumentnumber() : '',
      companyCityId: company ? company.getCityid() : 0,
      companyVerificationDigit: company ? company.getVerificationdigit() : 0,
      companyPostalCode: company ? company.getPostalcode() : '',
      companyContactName: company ? company.getContactname() : '',
    };
  }

  /**
   * update user profile data
   * @param {UserDataShared} userData
   */
  async updateUserProfileData(userData: UserDataShared): Promise<void> {
    const request: UpdateUserProfileDataRequest = new UpdateUserProfileDataRequest();
    request.setUserid(parseInt(userData.id, 10));
    request.setName(userData.name);
    request.setEmail(userData.email);
    request.setPhone(userData.userPhone);
    request.setCityid(userData.cityId);
    request.setDocumenttype(userData.documentType);
    request.setDocumentnumber(userData.documentNumber);
    request.setPostalcode(userData.postalcode);
    const response: UpdateProfileDataResponse = await this.updateUserProfileCall(request);

    if (typeof response?.getStatus !== 'function' || response?.getStatus().getStatus() !== StatusInfo.StatusType.SUCCESS) {
      throw new MuException({});
    }
  }

  /**
   * update user profile data
   * @param {UserDataShared} userData
   */
  async updateCompanyProfileData(userData: UserDataShared): Promise<void> {
    const request: UpdateCompanyProfileDataRequest = new UpdateCompanyProfileDataRequest();
    request.setUserid(parseInt(userData.id, 10));
    request.setName(userData.companyName);
    request.setDocumenttype(userData.companyDocumentType);
    request.setDocumentnumber(userData.companyDocumentNumber);
    request.setPhone(userData.companyPhone);
    request.setCityid(userData.companyCityId);
    request.setVerificationdigit(userData.companyVerificationDigit);
    request.setPostalcode(userData.companyPostalCode);
    request.setContactname(userData.companyContactName);
    const response: UpdateProfileDataResponse = await this.updateCompanyProfileCall(request);

    if (typeof response?.getStatus !== 'function' || response?.getStatus().getStatus() !== StatusInfo.StatusType.SUCCESS) {
      throw new MuException({});
    }
  }
}
